import type { StatusConfig } from '@/components/ui';

/**
 * EAA-1821: the audit screen's vocabulary — every action, credential mode and
 * target_type value below was confirmed against
 * services/portal-api/portal_api/directory/models.py (`AccountAudit`
 * ACTION_CHOICES, `AdminAudit` ACTION_CHOICES) and a grep of every
 * `AdminAuditService.record(...)` / `AccountAuditService.record(...)` call
 * site (those two fields have no server-side `choices`, so the sets here are
 * what is actually written, not a guess).
 *
 * Two representations live here on purpose:
 *   - `*_LABELS` — Vietnamese strings, used by the table/drawer CELLS
 *     (`auditCells.tsx`, `AuditDetailDrawer.tsx`), which look a raw code up
 *     synchronously outside a `t()` call.
 *   - `*_OPTIONS` — `{ value, labelKey }`, used by the FILTER BAR, which
 *     renders through `useTranslation()` and must follow the operator's
 *     language like the rest of the console.
 */

/** i18n namespaces for the three code vocabularies. */
const ACTION_KEY_PREFIX = 'console.audit.action.';
const CREDENTIAL_MODE_KEY_PREFIX = 'console.audit.credentialMode.';
const TARGET_TYPE_KEY_PREFIX = 'console.audit.targetType.';

/** One filter option before translation. `label` is resolved by the renderer. */
export interface AuditOption {
    value: string;
    labelKey: string;
}

function toOptions(values: string[], prefix: string): AuditOption[] {
    return values.map((value) => ({ value, labelKey: `${prefix}${value}` }));
}

/**
 * One flat map for both sources — the account (`create`/`delete`/`reset`/…)
 * and admin (`integration_*`/`config_*`/…) action vocabularies never collide,
 * so `AuditTable` can look up any row's `action` here regardless of
 * `row.kind`. Anything NOT in this map falls back to the raw code alone
 * (see `auditCells`' `ActionCell`) rather than a guessed label.
 */
export const ACTION_LABELS: Record<string, string> = {
    // AccountAudit.ACTION_CHOICES
    create: 'Tạo tài khoản',
    delete: 'Xoá tài khoản',
    reset: 'Đặt lại mật khẩu',
    self_change: 'Tự đổi mật khẩu',
    lock: 'Khoá',
    unlock: 'Mở khoá',
    // AdminAudit.ACTION_CHOICES
    integration_create: 'Tạo tích hợp',
    integration_update: 'Cập nhật tích hợp',
    integration_rotate: 'Xoay khoá tích hợp',
    integration_confirm_rotation: 'Xác nhận xoay khoá tích hợp',
    integration_deactivate: 'Vô hiệu hoá tích hợp',
    app_ci_token_rotate: 'Xoay token CI ứng dụng',
    registry_upsert: 'Cập nhật danh mục ứng dụng',
    config_stage: 'Soạn cấu hình (chờ xác nhận)',
    config_confirm: 'Xác nhận cấu hình',
    member_import: 'Nhập danh sách thành viên',
    erasure_stage: 'Soạn yêu cầu xoá dữ liệu',
    erasure_confirm: 'Xác nhận xoá dữ liệu',
};

/**
 * EAA-1821: the full `AccountAudit.ACTION_CHOICES`.
 *
 * This list used to stop at `create`/`delete`, with a comment explaining that
 * the backend ignored anything else. That was true and is the more interesting
 * half of the story: `AccountAuditListView.get` validated the incoming `action`
 * against a HARDCODED `(create, delete)` pair, so filtering on `reset` — or on
 * `lock`, added later — was dropped on the floor and the endpoint returned the
 * UNFILTERED list. An audit log that answers "here is everything" to "show me
 * the password resets" is worse than one that answers "nothing": the operator
 * cannot tell the filter failed. The view now validates against the model's own
 * `ACTION_CHOICES`, so every value below is a real, server-side filter.
 *
 * That is also why this list has to be kept level with the model: the last three
 * arrived from main (EAA-1861's user sync, EAA-1729's password claim) AFTER this
 * facet was built. The server picked them up for free — it validates against
 * `ACTION_CHOICES` — but an unlisted code has no chip entry to filter by and no
 * label, so the table would print the raw `sync_create` at the operator.
 */
const ACCOUNT_ACTION_VALUES = [
    'create',
    'delete',
    'reset',
    'self_change',
    'lock',
    'unlock',
    'sync_create',
    'sync_link',
    'password_claim',
];

/**
 * EAA-1821: `AdminAuditListView.get` validates against the full
 * `dict(AdminAudit.ACTION_CHOICES)` server-side, so every code below is a
 * real, working filter.
 */
const ADMIN_ACTION_VALUES = [
    'integration_create',
    'integration_update',
    'integration_rotate',
    'integration_confirm_rotation',
    'integration_deactivate',
    'app_ci_token_rotate',
    'registry_upsert',
    'config_stage',
    'config_confirm',
    'member_import',
    'erasure_stage',
    'erasure_confirm',
];

/**
 * EAA-1821: no synthetic `{ value: '', label: 'Tất cả' }` row any more. These
 * feed MULTI-select chips, where "all" is the empty selection, not a member of
 * the set — an `''` option in a checkbox list is a box you can tick alongside
 * `create`, which has no meaning. The word itself moved to the chip's
 * `allLabel` prop.
 */
export const ACCOUNT_ACTION_OPTIONS: AuditOption[] = toOptions(
    ACCOUNT_ACTION_VALUES,
    ACTION_KEY_PREFIX
);

export const ADMIN_ACTION_OPTIONS: AuditOption[] = toOptions(
    ADMIN_ACTION_VALUES,
    ACTION_KEY_PREFIX
);

/**
 * EAA-1821: `AccountAudit.credential_mode` — the new "Chế độ" facet. The two
 * values are the only ones the API can write: `AccountCreateSerializer`
 * declares `credential_mode = ChoiceField(choices=[INVITE, TEMP_PASSWORD])`
 * (org_serializers.py L526-527: `INVITE = "invite"`,
 * `TEMP_PASSWORD = "temp_password"`, mirrored by keycloak_admin.py L211-212),
 * and `AccountsView.post` passes that validated value straight into
 * `AccountAuditService.record(..., credential_mode=mode)`. Every other audited
 * account action records the column blank.
 */
const CREDENTIAL_MODE_VALUES = ['temp_password', 'invite'];

export const CREDENTIAL_MODE_LABELS: Record<string, string> = {
    temp_password: 'Mật khẩu tạm',
    invite: 'Mời qua email',
};

export const CREDENTIAL_MODE_OPTIONS: AuditOption[] = toOptions(
    CREDENTIAL_MODE_VALUES,
    CREDENTIAL_MODE_KEY_PREFIX
);

/**
 * EAA-1821: `AdminAudit.target_type` is a free `CharField` (no server
 * `choices`) — grepped every write site under directory/*_views.py and
 * services.py; these five are the only values ever written today. Kept as a
 * map (not a union type) so an unmapped future value still renders instead
 * of throwing.
 */
export const TARGET_TYPE_LABELS: Record<string, string> = {
    external_system: 'Hệ thống ngoài',
    application: 'Ứng dụng',
    runtime_config: 'Cấu hình thời gian chạy',
    erasure_request: 'Yêu cầu xoá dữ liệu',
    organization: 'Cơ sở',
};

export const TARGET_TYPE_OPTIONS: AuditOption[] = toOptions(
    ['external_system', 'application', 'runtime_config', 'erasure_request', 'organization'],
    TARGET_TYPE_KEY_PREFIX
);

/**
 * EAA-1821: "Kết quả" is a two-value facet the backend takes as the string
 * `success=true|false`, so its option values are those literals rather than
 * booleans — `AuditFilters.success` carries the wire value unchanged.
 */
export const SUCCESS_OPTIONS: AuditOption[] = [
    { value: 'true', labelKey: 'console.audit.result.success' },
    { value: 'false', labelKey: 'console.audit.result.failure' },
];

/**
 * EAA-1821: shared by `AuditTable`'s "Kết quả" column and
 * `AuditDetailDrawer`'s equivalent field — kept here (a plain-data file, no
 * JSX) rather than in `auditCells.tsx` so that file stays component-only for
 * React Fast Refresh (a mixed file triggers `react-refresh/only-export-components`).
 */
export const RESULT_STATUS_CONFIG: Record<string, StatusConfig> = {
    success: { label: 'Thành công', tone: 'success' },
    failure: { label: 'Thất bại', tone: 'error' },
};
